import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import * as XLSX from 'xlsx';

const PRESET_TAGS = [
  'Yönetmen', 'Yapımcı', 'Oyuncu', 'Müzisyen', 'Fotoğrafçı',
  'Festival', 'Kurum', 'Basın', 'Ortak', 'Teknik', 'Hukuk', 'Finans',
  'Berlin', 'İstanbul', 'New York', 'London', 'Paris'
];

const PRESET_COLORS = [
  '#6c5ce7','#e17055','#0984e3','#d63031','#00b894','#fdcb6e',
  '#636e72','#e84393','#2d9cdb','#27ae60','#8e44ad','#16a085',
  '#f39c12','#c0392b','#1abc9c','#2980b9','#8e44ad','#e67e22',
];

function tagColor(tag) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return PRESET_COLORS[Math.abs(hash) % PRESET_COLORS.length];
}

const S = {
  ink: '#111', mute: '#888', border: '#e8e4de', borderLight: '#f0ece6',
  bg: '#f6f3ee', card: '#fff', accent: '#6c5ce7',
  mono: { fontFamily: "'JetBrains Mono', monospace" },
};

function TagBadge({ tag, onRemove, small }) {
  const color = tagColor(tag);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      background: color + '18', color,
      border: `1px solid ${color}44`,
      borderRadius: 3, padding: small ? '1px 6px' : '2px 8px',
      fontSize: small ? 10 : 11, fontWeight: 600, letterSpacing: '0.03em', whiteSpace: 'nowrap'
    }}>
      {tag}
      {onRemove && <span onClick={onRemove} style={{ cursor: 'pointer', opacity: 0.5, fontSize: 13, lineHeight: 1 }}>×</span>}
    </span>
  );
}

function TagSelector({ selected, onChange, allExisting }) {
  const [custom, setCustom] = useState('');
  const toggle = (tag) => onChange(selected.includes(tag) ? selected.filter(t => t !== tag) : [...selected, tag]);
  const addCustom = () => {
    const val = custom.trim();
    if (val && !selected.includes(val)) onChange([...selected, val]);
    setCustom('');
  };
  const allOptions = [...new Set([...PRESET_TAGS, ...(allExisting || [])])].sort();
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
        {allOptions.map(tag => {
          const active = selected.includes(tag);
          const color = tagColor(tag);
          return (
            <span key={tag} onClick={() => toggle(tag)} style={{
              cursor: 'pointer', padding: '3px 9px', borderRadius: 3, fontSize: 11, fontWeight: 600,
              background: active ? color + '18' : 'transparent',
              color: active ? color : S.mute,
              border: `1px solid ${active ? color + '66' : S.border}`,
              transition: 'all 0.12s'
            }}>{tag}</span>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={custom} onChange={e => setCustom(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom())}
          placeholder="Yeni etiket..."
          style={{ flex: 1, background: S.bg, border: `1px solid ${S.border}`, borderRadius: 3, color: S.ink, padding: '5px 10px', fontSize: 12, outline: 'none' }} />
        <button onClick={addCustom} style={{ background: S.ink, border: 'none', color: '#fff', borderRadius: 3, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>Ekle</button>
      </div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {selected.map(tag => <TagBadge key={tag} tag={tag} onRemove={() => toggle(tag)} />)}
        </div>
      )}
    </div>
  );
}

function ContactForm({ initial, onSave, onCancel, allTags }) {
  const [form, setForm] = useState(initial ? { ...initial, tags: initial.tags || [] } : { name: '', phone: '', email: '', tags: [], notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const field = { background: S.bg, border: `1px solid ${S.border}`, borderRadius: 3, color: S.ink, padding: '7px 10px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lbl = { ...S.mono, fontSize: 9, color: S.mute, marginBottom: 4, display: 'block', letterSpacing: '0.12em', textTransform: 'uppercase' };
  return (
    <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 4, padding: 20, marginBottom: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>İsim</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ad Soyad" style={field} /></div>
        <div><label style={lbl}>Telefon</label><input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+49 123 456 7890" style={field} /></div>
        <div><label style={lbl}>E-posta</label><input value={form.email} onChange={e => set('email', e.target.value)} placeholder="ornek@mail.com" style={field} /></div>
        <div><label style={lbl}>Not</label><input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Kısa not..." style={field} /></div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Etiketler</label>
        <TagSelector selected={form.tags} onChange={v => set('tags', v)} allExisting={allTags} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ background: 'transparent', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 3, padding: '6px 16px', fontSize: 12, cursor: 'pointer' }}>İptal</button>
        <button onClick={() => (form.name.trim() || form.email.trim()) && onSave(form)} style={{ background: S.ink, border: 'none', color: '#fff', borderRadius: 3, padding: '6px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Kaydet</button>
      </div>
    </div>
  );
}

function BulkTagModal({ count, allTags, onApply, onClose }) {
  const [mode, setMode] = useState('add');
  const [selected, setSelected] = useState([]);
  const toggle = (tag) => setSelected(s => s.includes(tag) ? s.filter(t => t !== tag) : [...s, tag]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 6, padding: 24, width: 480, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Toplu Etiket Düzenle</div>
        <div style={{ ...S.mono, fontSize: 10, color: S.mute, marginBottom: 16 }}>{count} kişi seçili</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['add','Ekle'],['remove','Çıkar'],['replace','Değiştir']].map(([v,l]) => (
            <button key={v} onClick={() => setMode(v)} style={{
              padding: '5px 14px', borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: mode === v ? S.ink : 'transparent', color: mode === v ? '#fff' : S.mute,
              border: `1px solid ${mode === v ? S.ink : S.border}`,
            }}>{l}</button>
          ))}
        </div>
        <div style={{ ...S.mono, fontSize: 9, color: S.mute, marginBottom: 8, letterSpacing: '0.1em' }}>
          {mode === 'add' ? 'EKLENECEK ETİKETLER' : mode === 'remove' ? 'ÇIKARILACAK ETİKETLER' : 'YENİ ETİKET SETİ'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 20 }}>
          {[...new Set([...PRESET_TAGS, ...allTags])].sort().map(tag => {
            const active = selected.includes(tag);
            const color = tagColor(tag);
            return (
              <span key={tag} onClick={() => toggle(tag)} style={{
                cursor: 'pointer', padding: '3px 10px', borderRadius: 3, fontSize: 11, fontWeight: 600,
                background: active ? color + '18' : 'transparent', color: active ? color : S.mute,
                border: `1px solid ${active ? color + '66' : S.border}`,
              }}>{tag}</span>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 3, padding: '6px 16px', fontSize: 12, cursor: 'pointer' }}>İptal</button>
          <button onClick={() => selected.length > 0 && onApply(mode, selected)} style={{ background: S.ink, border: 'none', color: '#fff', borderRadius: 3, padding: '6px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Uygula</button>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ contact, selected, onToggle, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const initial = (contact.name || contact.email || '?').charAt(0).toUpperCase();
  const firstTag = (contact.tags || [])[0];
  const color = firstTag ? tagColor(firstTag) : S.accent;
  return (
    <div style={{
      background: selected ? '#f5f3ff' : S.card,
      border: `1px solid ${selected ? '#c5b8fa' : S.borderLight}`,
      borderRadius: 3, padding: '8px 12px', marginBottom: 2,
      transition: 'all 0.12s', display: 'flex', alignItems: 'flex-start', gap: 10
    }}>
      <input type="checkbox" checked={selected} onChange={onToggle} style={{ marginTop: 9, cursor: 'pointer', accentColor: S.accent, flexShrink: 0 }} />
      <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: color + '18', border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color, marginTop: 2 }}>{initial}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: S.ink }}>
            {contact.name || <span style={{ color: S.mute, fontStyle: 'italic' }}>İsimsiz</span>}
          </div>
          {contact.email && <a href={`mailto:${contact.email}`} onClick={e => e.stopPropagation()} style={{ color: S.accent, fontSize: 12, textDecoration: 'none' }}>{contact.email}</a>}
          {contact.phone && <a href={`tel:${contact.phone}`} onClick={e => e.stopPropagation()} style={{ color: S.mute, fontSize: 12, textDecoration: 'none' }}>{contact.phone}</a>}
        </div>
        {(contact.tags || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
            {contact.tags.map(tag => <TagBadge key={tag} tag={tag} small />)}
          </div>
        )}
        {expanded && contact.notes && (
          <div style={{ marginTop: 6, padding: '6px 10px', background: S.bg, borderRadius: 3, fontSize: 12, color: '#555', borderLeft: `2px solid ${S.border}` }}>{contact.notes}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
        {contact.notes && <button onClick={() => setExpanded(e => !e)} style={{ background: expanded ? S.bg : 'transparent', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 3, padding: '3px 7px', fontSize: 10, cursor: 'pointer' }}>not</button>}
        <button onClick={() => onEdit(contact)} style={{ background: 'transparent', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 3, padding: '3px 7px', fontSize: 10, cursor: 'pointer' }}>düzenle</button>
        <button onClick={() => onDelete(contact.id)} style={{ background: 'transparent', border: '1px solid #fde8e8', color: '#c0392b', borderRadius: 3, padding: '3px 7px', fontSize: 10, cursor: 'pointer' }}>sil</button>
      </div>
    </div>
  );
}

function BatchSection({ batch, contacts, selected, onToggle, onEdit, onDelete, onDeleteBatch, allFilteredSelected, onToggleAll }) {
  const [collapsed, setCollapsed] = useState(false);
  const batchColor = tagColor(batch.name);
  const date = new Date(batch.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Batch header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `2px solid ${batchColor}33`, marginBottom: 8 }}>
        <input type="checkbox" checked={allFilteredSelected} onChange={onToggleAll} style={{ cursor: 'pointer', accentColor: S.accent }} />
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: batchColor, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: S.ink }}>{batch.name}</span>
          <span style={{ ...S.mono, fontSize: 10, color: S.mute, marginLeft: 10 }}>{contacts.length} kişi · {date}</span>
        </div>
        <button onClick={() => setCollapsed(c => !c)} style={{ ...S.mono, background: 'transparent', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 3, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
          {collapsed ? '▸ Göster' : '▾ Gizle'}
        </button>
        <button onClick={() => onDeleteBatch(batch.id, batch.name, contacts.length)} style={{ ...S.mono, background: 'transparent', border: '1px solid #fde8e8', color: '#c0392b', borderRadius: 3, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>
          Sayfayı Sil
        </button>
      </div>
      {!collapsed && contacts.map(contact => (
        <ContactRow
          key={contact.id} contact={contact}
          selected={selected.has(contact.id)}
          onToggle={() => onToggle(contact.id)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export default function ContactsView() {
  const [batches, setBatches] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const fileRef = useRef();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: batchData }, { data: contactData }] = await Promise.all([
      supabase.from('contact_batches').select('*').order('created_at', { ascending: false }),
      supabase.from('contacts').select('*').order('name'),
    ]);
    setBatches(batchData || []);
    setContacts(contactData || []);
    setLoading(false);
  };

  const saveContact = async (form) => {
    if (editContact) {
      await supabase.from('contacts').update(form).eq('id', editContact.id);
    } else {
      // Manual adds go into a "Manuel" batch
      let manualBatch = batches.find(b => b.name === 'Manuel Eklenenler');
      if (!manualBatch) {
        const { data } = await supabase.from('contact_batches').insert([{ name: 'Manuel Eklenenler' }]).select().single();
        manualBatch = data;
      }
      await supabase.from('contacts').insert([{ ...form, batch_id: manualBatch.id }]);
    }
    setShowForm(false);
    setEditContact(null);
    fetchAll();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Bu kişiyi silmek istediğinize emin misiniz?')) return;
    await supabase.from('contacts').delete().eq('id', id);
    setSelected(s => { const n = new Set(s); n.delete(id); return n; });
    fetchAll();
  };

  const deleteBatch = async (batchId, batchName, count) => {
    if (!window.confirm(`"${batchName}" sayfasını ve içindeki ${count} kişiyi silmek istediğinize emin misiniz?`)) return;
    await supabase.from('contact_batches').delete().eq('id', batchId);
    setSelected(new Set());
    fetchAll();
  };

  const parseTags = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return String(val).split(/[,;|]/).map(t => t.trim()).filter(Boolean);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const rawName = file.name.replace(/\.[^/.]+$/, '');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const { data: existing } = await supabase.from('contacts').select('email');
      const existingEmails = new Set((existing || []).map(c => (c.email || '').toLowerCase().trim()).filter(Boolean));

      const toInsert = [], skipped = [];
      for (const row of rows) {
        const name  = String(row['NAME'] || row['Name'] || row['name'] || row['İsim'] || row['isim'] || '').trim();
        const email = String(row['MAIL'] || row['Mail'] || row['mail'] || row['Email'] || row['email'] || '').trim();
        const phone = String(row['PHONE'] || row['Telefon'] || row['telefon'] || row['Phone'] || row['phone'] || '').trim();
        const connection = String(row['CONNECTION'] || row['connection'] || '').trim();
        const data_col   = String(row['DATA'] || row['data'] || '').trim();
        const rawNotes   = String(row['NOTES'] || row['Not'] || row['not'] || row['Notes'] || row['notes'] || '').trim();
        const tags  = parseTags(row['Etiket'] || row['etiket'] || row['Tag'] || row['Tags'] || row['tags'] || '');
        const notes = [rawNotes, connection, data_col].filter(Boolean).join(' | ');
        if (!name && !email && !phone) continue;
        if (email && existingEmails.has(email.toLowerCase())) { skipped.push(email || name); continue; }
        if (email) existingEmails.add(email.toLowerCase());
        toInsert.push({ name, phone, email, tags, notes });
      }

      if (toInsert.length > 0) {
        const { data: batch } = await supabase.from('contact_batches').insert([{ name: rawName }]).select().single();
        await supabase.from('contacts').insert(toInsert.map(r => ({ ...r, batch_id: batch.id })));
        fetchAll();
      }

      let msg = `${toInsert.length} kişi aktarıldı.`;
      if (skipped.length > 0) msg += `\n${skipped.length} atlandı (zaten kayıtlı): ${skipped.slice(0,5).join(', ')}${skipped.length > 5 ? '…' : ''}`;
      alert(msg);
    } catch (err) { alert('Import hatası: ' + err.message); }
    setImporting(false);
    e.target.value = '';
  };

  const handleExport = () => {
    const rows = contacts.map(c => ({
      'NAME': c.name, 'PHONE': c.phone || '', 'MAIL': c.email || '',
      'NOTES': c.notes || '', 'TAGS': (c.tags || []).join(', '),
      'BATCH': batches.find(b => b.id === c.batch_id)?.name || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kişiler');
    XLSX.writeFile(wb, 'fol-contacts.xlsx');
  };

  const handleBulkTag = async (mode, tags) => {
    const ids = [...selected];
    for (const id of ids) {
      const contact = contacts.find(c => c.id === id);
      if (!contact) continue;
      let newTags;
      if (mode === 'add') newTags = [...new Set([...(contact.tags || []), ...tags])];
      else if (mode === 'remove') newTags = (contact.tags || []).filter(t => !tags.includes(t));
      else newTags = tags;
      await supabase.from('contacts').update({ tags: newTags }).eq('id', id);
    }
    setShowBulk(false);
    setSelected(new Set());
    fetchAll();
  };

  const toggleTag = (tag) => setActiveTags(p => p.includes(tag) ? p.filter(t => t !== tag) : [...p, tag]);
  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allTags = [...new Set(contacts.flatMap(c => c.tags || []))].sort();

  const filteredContacts = contacts.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || (c.name||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q) || (c.phone||'').includes(q) || (c.notes||'').toLowerCase().includes(q) || (c.tags||[]).some(t=>t.toLowerCase().includes(q));
    const matchTags = activeTags.length === 0 || activeTags.every(t => (c.tags||[]).includes(t));
    return matchSearch && matchTags;
  });

  // Group filtered contacts by batch; contacts without batch go into a virtual "no batch" group
  const batchGroups = [];
  const batchMap = {};
  for (const b of batches) batchMap[b.id] = [];
  const noBatch = [];
  for (const c of filteredContacts) {
    if (c.batch_id && batchMap[c.batch_id]) batchMap[c.batch_id].push(c);
    else noBatch.push(c);
  }
  for (const b of batches) {
    if (batchMap[b.id].length > 0) batchGroups.push({ batch: b, contacts: batchMap[b.id] });
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ ...S.mono, fontSize: 9, color: S.mute, marginBottom: 4, letterSpacing: '0.15em' }}>CONTACTS</div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>Kişiler</div>
            <div style={{ ...S.mono, fontSize: 10, color: S.mute, marginTop: 2 }}>{contacts.length} kişi · {batches.length} sayfa</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {selected.size > 0 && (
              <button onClick={() => setShowBulk(true)} style={{ background: S.accent, border: 'none', color: '#fff', borderRadius: 2, padding: '5px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', ...S.mono }}>
                {selected.size} seçili — Etiket Düzenle
              </button>
            )}
            <button onClick={() => fileRef.current.click()} disabled={importing} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 2, padding: '5px 12px', fontSize: 11, cursor: 'pointer', ...S.mono }}>↑ İçe Aktar</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display: 'none' }} />
            <button onClick={handleExport} style={{ background: 'none', border: `1px solid ${S.border}`, color: S.mute, borderRadius: 2, padding: '5px 12px', fontSize: 11, cursor: 'pointer', ...S.mono }}>↓ Dışa Aktar</button>
            <button onClick={() => { setEditContact(null); setShowForm(s => !s); }} style={{ background: S.ink, border: 'none', color: '#fff', borderRadius: 2, padding: '5px 14px', fontSize: 11, fontWeight: 500, cursor: 'pointer', ...S.mono }}>
              {showForm && !editContact ? '× İptal' : '+ Yeni Kişi'}
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && <ContactForm initial={editContact} onSave={saveContact} allTags={allTags} onCancel={() => { setShowForm(false); setEditContact(null); }} />}

        {/* Search */}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="İsim, mail, telefon, not veya etiket ara..."
          style={{ width: '100%', background: S.card, border: `1px solid ${S.border}`, borderRadius: 3, color: S.ink, padding: '9px 14px', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />

        {/* Tag filter */}
        {allTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 20 }}>
            <span onClick={() => setActiveTags([])} style={{
              cursor: 'pointer', padding: '3px 10px', borderRadius: 3, fontSize: 11, fontWeight: 600, ...S.mono,
              background: activeTags.length === 0 ? S.ink : 'transparent',
              color: activeTags.length === 0 ? '#fff' : S.mute,
              border: `1px solid ${activeTags.length === 0 ? S.ink : S.border}`,
            }}>Tümü</span>
            {allTags.map(tag => {
              const active = activeTags.includes(tag);
              const color = tagColor(tag);
              const count = contacts.filter(c => (c.tags||[]).includes(tag)).length;
              return (
                <span key={tag} onClick={() => toggleTag(tag)} style={{
                  cursor: 'pointer', padding: '3px 10px', borderRadius: 3, fontSize: 11, fontWeight: 600,
                  background: active ? color + '18' : 'transparent', color: active ? color : S.mute,
                  border: `1px solid ${active ? color + '55' : S.border}`,
                }}>
                  {tag} <span style={{ opacity: 0.4, fontSize: 10 }}>{count}</span>
                </span>
              );
            })}
          </div>
        )}

        {/* List grouped by batch */}
        {loading ? (
          <div style={{ ...S.mono, fontSize: 11, color: S.mute, padding: '40px 0', textAlign: 'center' }}>Yükleniyor…</div>
        ) : filteredContacts.length === 0 ? (
          <div style={{ ...S.mono, fontSize: 11, color: S.mute, padding: '40px 0', textAlign: 'center' }}>
            {search || activeTags.length > 0 ? 'Eşleşen kişi bulunamadı.' : 'Henüz kişi eklenmedi.'}
          </div>
        ) : (
          <>
            {batchGroups.map(({ batch, contacts: bc }) => {
              const allSel = bc.length > 0 && bc.every(c => selected.has(c.id));
              return (
                <BatchSection
                  key={batch.id} batch={batch} contacts={bc}
                  selected={selected} onToggle={toggleSelect}
                  onEdit={c => { setEditContact(c); setShowForm(true); }}
                  onDelete={deleteContact}
                  onDeleteBatch={deleteBatch}
                  allFilteredSelected={allSel}
                  onToggleAll={() => {
                    if (allSel) setSelected(s => { const n = new Set(s); bc.forEach(c => n.delete(c.id)); return n; });
                    else setSelected(s => { const n = new Set(s); bc.forEach(c => n.add(c.id)); return n; });
                  }}
                />
              );
            })}
            {noBatch.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ ...S.mono, fontSize: 10, color: S.mute, borderBottom: `1px solid ${S.border}`, paddingBottom: 6, marginBottom: 8 }}>Sayfa atanmamış</div>
                {noBatch.map(contact => (
                  <ContactRow key={contact.id} contact={contact} selected={selected.has(contact.id)}
                    onToggle={() => toggleSelect(contact.id)}
                    onEdit={c => { setEditContact(c); setShowForm(true); }}
                    onDelete={deleteContact} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showBulk && <BulkTagModal count={selected.size} allTags={allTags} onApply={handleBulkTag} onClose={() => setShowBulk(false)} />}
    </div>
  );
}
